/**
 * modals.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, Injectable } from "@angular/core";
import { DialogRef, ModalComponent, overlayConfigFactory }
  from "angular2-modal";
import { BSModalContext, Modal, TwoButtonPreset }
  from "angular2-modal/plugins/bootstrap";
import { Observable, Observer } from "rxjs/Rx";

// TODO: WHY THE HECK ISN'T THIS JUST A SERVICE? THE PLUG-YOUR-MODAL-DEP-IN
// PATTERN IS TOTALLY WEIRD AND COULD JUST BE WRAPPED INTO SIMPLE CALLS.
// PAST TAYLOR MAKES WEIRD DECISIONS! [TDHS]

/**
 * Maximum number of levels to allow tree operations upon.
 * @type {number}
 */
const DEPTH_LIMIT : number = 1000;

/**
 * {@link ModalHelpers} provides utility functions for dispatching
 * @type {[type]}
 */
export class ModalHelpers
{
  /**
   * Convert a modal dialog promise into an observable returning true if the
   * dialog was *closed* and false if it was *dismissed*
   * @param  {Promise<DialogRef<any>>} modalPromise the result of modal.open()
   * @return {Observable<boolean>}
   */
  public static observeBooleanConfirmation (
    modalPromise : Promise<DialogRef<any>>
  ) : Observable<boolean>
  {
    return Observable.create((o : Observer<boolean>) => {
      modalPromise.then((dialog : DialogRef<any>) => {
        dialog.result.then(
          (result : any) => (o.next(true) || o.complete()),
          () => (o.next(false) || o.complete())
        );
      });
    });
  }

  /**
   * Convert a modal dialog promise into an observable returning the dialog's
   * result value. If the modal is dismissed the observable will return the
   * dismiss value (if set) or complete immediates (if not provided).
   * @param  {Promise<DialogRef<any>>} modalPromise the result of modal.open()
   * @return {Observable<any>}
   */
  public static observeResult<T> (
    modalPromise : Promise<DialogRef<any>>,
    dismissValue? : any
  ) : Observable<T>
  {
    return Observable.create((o : Observer<T>) => {
      modalPromise.then((dialog : DialogRef<any>) => {
        dialog.result.then(
          (result : any) => {
            o.next(result);
            o.complete();
          },
          () => {
            if (dismissValue !== undefined)
            {
              o.next(dismissValue);
            }
            o.complete();
          }
        );
      });
    });
  }

  /**
   * A simple function that removes the need for non-service modal imports when
   * opening a modal from a custom component.
   * @param  {Modal}   modalService   the Modal singleton
   * @param  {any}     componentClass the component for the modal
   * @param  {Object}  configuration  configuration values for the context
   * @param  {any}     context        the base context object
   * @return {Promise}
   */
  public static openCustom (
    modalService : Modal,
    componentClass : any,
    configuration : {[key : string] : any} = {},
    context : any = BSModalContext
  ) : Promise<DialogRef<any>>
  {
    return modalService.open(
      componentClass,
      overlayConfigFactory(configuration, context)
    );
  }

  public static observeCustom<T> (
    modalService : Modal,
    componentClass : any,
    configuration : {[key : string] : any},
  ) : Observable<T>
  {
    return ModalHelpers.observeResult<T>(
      ModalHelpers.openCustom(
        modalService,
        componentClass,
        configuration
      ),
      null
    );
  }

  public static alert (modalService : Modal, title : string, message : string)
    : Promise<DialogRef<any>>
  {
    return modalService.alert()
      .size("sm")
      .title(title)
      .body(message)
      .open();
  }
}

@Injectable()
export class UseModalsForBrowserAlerts
{
  private browserAlert : (msg : string) => void = alert;

  private alertWithModal (msg : string) : void
  {
    ModalHelpers.alert(this.modal, "Warning!", msg);
  }

  public constructor (private modal : Modal)
  {
    window.alert = (msg : string) => this.alertWithModal(msg);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/utilities/modals.ts